import { get } from "lodash";
import { SHADOW, COLORS } from "@treelab/panda";

import Chart, { IChartProps } from "./Chart";

import { IChartFormValue, StatisticsType } from "../types/form-type";
import { ITableData, IWrokspaceData } from "../types/table-type";
import { IDatasetSource, IDatasetTransform, ITooltipFormatterParams } from "../types/echarts-type";
import { ChartErrorType } from "../types/global-type";

import {
  getPieTooltipDom,
  getPieCenterAndRadius,
  getTooltipPosition,
  getOptionColorByValue,
} from "../utils/option_main";
import { getDimensionOptions, getYAxisOptions } from "../utils/form_secondary";

import {
  PIE_DIMENSION_LIMIT,
  PIE_LABEL_LINE_LENGTH2,
  PIE_SELECTED_OFFSET,
  LEGEND_PAGE_ICON_PATH,
  LEGEND_PAGE_ICON_SIZE,
} from "../constants/chart";
import { getColors } from "../utils/colors";

abstract class BasicPieChart extends Chart {
  private legendCount: number;
  constructor(props: IChartProps) {
    super(props);
    this.legendCount = 0;
  }

  public checkChartOptionError(
    formValue: IChartFormValue,
    tableData: ITableData,
    workspaceData: IWrokspaceData
  ): ChartErrorType | null {
    const { dimensionId, statisticsType, calculationTargetId } = formValue;
    const xColumnList = getDimensionOptions(tableData);
    const yColumnList = getYAxisOptions(tableData);
    if (!xColumnList.find((item) => item.value === dimensionId)) return ChartErrorType.SOME_FIELDS_ARE_INVALID;
    if (
      statisticsType === StatisticsType.STATISTIC_COLUMN &&
      !yColumnList.find((item) => item.value === calculationTargetId)
    )
      return ChartErrorType.SOME_FIELDS_ARE_INVALID;
    if (this.legendCount > PIE_DIMENSION_LIMIT) return ChartErrorType.PIE_DIMENSION_COUNT_LIMIT;
    return null;
  }

  public getTooltip() {
    const canvasWidth = this.getCanvasWidth();
    const tooltip = {
      trigger: "item",
      backgroundColor: "rgba(255, 255, 255, 0.98)",
      borderWidth: 0,
      padding: [10, 12],
      extraCssText: SHADOW.overlay1,
      formatter: (params: ITooltipFormatterParams | ITooltipFormatterParams[], ticket: string) => {
        const newParams = this.convertTooltipParams(params);
        const dom = getPieTooltipDom(newParams, canvasWidth, this.formatValue);
        return dom;
      },
      position: getTooltipPosition,
    };
    return tooltip;
  }

  public calculationOther(dataset: (IDatasetSource | IDatasetTransform)[]) {
    const source = (get(dataset, "[0].source") as string[][]) || [];
    const sourceCount = source.length;
    this.legendCount = sourceCount;
  }

  public getLegend(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData) {
    const { isShowLegend } = formValue;
    const legend = isShowLegend
      ? {
        type: "scroll",
        show: true,
        bottom: this.isExpand ? 32 : 24,
        icon: "circle",
        width: this.getLegendContainerWidth(),
        itemWidth: 9,
        textStyle: {
          color: `${COLORS.grey}`,
          fontSize: 12,
          lineHeight: 16,
          overflow: "truncate",
        },
        formatter: (name: any) => {
          return this.basicFormatLegend(name);
        },
        pageIconColor: "rgba(131, 134, 143, 1)",
        pageIconInactiveColor: "rgba(162, 165, 177, 0.6)",
        pageTextStyle: {
          color: "rgba(131, 134, 143, 1)",
          fontWeight: 400,
        },
        pageIcons: LEGEND_PAGE_ICON_PATH,
        pageIconSize: LEGEND_PAGE_ICON_SIZE,
      }
      : { show: false };
    return legend;
  }

  public getPieSortTransform(dimension: string) {
    return {
      transform: [
        {
          type: "sort",
          config: {
            dimension: dimension,
            order: "desc",
          },
        },
      ],
    };
  }

  public getYAxis() {
    return { show: false };
  }

  public getXAxis() {
    return { show: false };
  }

  public getSeriesLabel(isShowLabel: boolean, isExpand: boolean) {
    const label = isShowLabel
      ? {
        show: true,
        alignTo: "edge",
        edgeDistance: isExpand ? 32 : 24,
        formatter: (params: { value: string | number[] }) => {
          const { value } = params;
          return `${this.formatDimension(value[0])}: ${this.formatValue(value[1] as number)}`;
        },
        fontSize: 12,
        lineHeight: 16,
        color: COLORS.grey,
      }
      : { show: false };
    return label;
  }

  public getBasicPieSeries(formValue: IChartFormValue) {
    const { isShowLabel = false } = formValue;
    const canvasWidth = this.getCanvasWidth();
    const canvasHeight = this.getCanvasHeight();
    const { center, radius } = getPieCenterAndRadius(
      canvasWidth,
      canvasHeight,
      formValue.isShowLabel,
      this.isExpand,
      this.formValue.isShowLegend
    );
    const label = this.getSeriesLabel(isShowLabel, this.isExpand);
    const seriesConfig = {
      type: "pie",
      label,
      selectedMode: "single",
      itemStyle: {
        borderColor: "#fff",
        borderWidth: 2,
        borderRadius: [0, 2, 2, 2],
        // 避免饼图中间出现 border 形成的小尖
        borderJoin: "round",
      },
      labelLine: {
        show: label.show,
        length2: PIE_LABEL_LINE_LENGTH2,
      },
      datasetIndex: 1,
      left: 0,
      top: 0,
      center,
      radius,
      selectedOffset: PIE_SELECTED_OFFSET,
      // 限制 pie chart 组件高度 包括 label 但由于圆是手动定位 优先级高于该属性 所以该属性主要用于限制 label
      // 根据 canvasHeight 和 是否有图例 进行计算
      height: this.getCanvasHeight() - (this.isExpand ? 80 : 60),
      animationDuration: 680,
    };

    return seriesConfig;
  }

  public getGrid() {
    const grid = {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    };
    return grid;
  }

  /**
   * 对于饼图 需要使用 categories 和 color 实现
   */
  public getVisualMap(formValue: IChartFormValue, tableData: ITableData, workspaceData: IWrokspaceData) {
    if (!formValue.useOptionColor) {
      return null;
    }

    if (!this.checkDimensionColumnHasColor()) {
      return null;
    }

    const colorByOptionId = getOptionColorByValue({ columns: tableData.columns, columnId: formValue.dimensionId });

    const source = (get(this.datasetObj, "[0].source") as string[][]) || [];

    const categories = new Array<string>(source.length).fill("");
    const color = new Array<string>(source.length).fill("");

    source.forEach((item, index) => {
      categories[index] = item[0];
      color[index] = colorByOptionId[item[0]] || getColors(index, item[0]);
    });

    const visualMap = {
      dimension: 0,
      type: "piecewise",
      categories,
      color: color.reverse(),//这里是一个大坑，饼图的颜色顺序是反的！！！！！！
      show: false,
    };

    return visualMap;
  }
}

export default BasicPieChart;
